import React, { useEffect, useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import Stepper from "@mui/material/Stepper";
import { api } from "../../../../api/apiClient";
import config from "../../../../config";
import {
  PartyAddress,
  RegistrationMemberType,
  RegistrationRequirementLevel,
  RoleType,
  ServiceAgreementTemplateDto,
  ServiceCategory,
  ServiceDto,
  ServiceRegistrationJourneyDto,
  ServiceRegistrationRequirementDto,
  ServiceRoleDto,
  ServiceAttributeCategory,
} from "../../../types/types";
import { useStaticData } from "../../../../context/StaticDataContext";
import { useAccess } from "../../../../context/AccessContext";
import { useAuth } from "../../../../context/AuthContext";
import { FEATURE_CODES, FeatureCode } from "../../../../constants/constants";
import { DraftServiceGroup } from "./ServiceGroupsCard";
import { DraftServiceRole, ServiceFormValue } from "./serviceFormTypes";
import { ServiceAgreementsStep } from "./steps/ServiceAgreementsStep";
import { ServiceAttributesStep } from "./steps/ServiceAttributesStep";
import { ServiceBrandingStep } from "./steps/ServiceBrandingStep";
import { ServiceDetailsStep } from "./steps/ServiceDetailsStep";
import { ServiceGroupsStep } from "./steps/ServiceGroupsStep";
import { ServiceRegistrationStep } from "./steps/ServiceRegistrationStep";
import { ServiceTeamRolesStep } from "./steps/ServiceTeamRolesStep";

import "../../../../css/buttons.css";
import "../../../../css/forms.css";
import "../../../../css/cards.css";
import "./CreateServiceForm.css";

/* =========================
 * Props
 * ========================= */

interface ServiceFormProps {
  tenantId: number;
  mode: "create" | "edit";
  service?: ServiceDto;
  onNameChange: (name: string) => void;
  onSaved: () => void;
  onCancel: () => void;
}

/* =========================
 * Colour palette
 * ========================= */

const DEFAULT_SERVICE_COLOUR = "#037D41";
const HEX_COLOUR_REGEX = /^#[0-9A-F]{6}$/;
const STANDARD_SERVICE_USER_ROLE_NAME = "Service User";
const SERVICE_FORM_STEPS: Array<{ label: string; feature: FeatureCode }> = [
  { label: "Details", feature: FEATURE_CODES.SERVICE_DETAILS },
  { label: "Attributes", feature: FEATURE_CODES.SERVICE_ATTRIBUTES },
  { label: "Branding", feature: FEATURE_CODES.SERVICE_BRANDING },
  { label: "Registration", feature: FEATURE_CODES.SERVICE_REGISTRATION },
  { label: "Agreements & Consents", feature: FEATURE_CODES.SERVICE_AGREEMENTS_CONSENTS },
  { label: "Team Roles", feature: FEATURE_CODES.SERVICE_TEAM_ROLES },
  { label: "Groups & Classes", feature: FEATURE_CODES.SERVICE_GROUPS_CLASSES },
];

const REGISTRATION_COMPONENT_FALLBACKS: Record<
  string,
  { label: string; description: string }
> = {
  PrimaryContact: {
    label: "Primary contact",
    description: "Captures the main adult or person being registered.",
  },
  ContactDetails: {
    label: "Contact details",
    description: "Captures phone and email details.",
  },
  Address: {
    label: "Address",
    description: "Captures the person or household address.",
  },
  Household: {
    label: "Family / household",
    description: "Captures family or household relationships.",
  },
  YouthProfile: {
    label: "Child profile",
    description: "Captures child-specific youth profile information.",
  },
  EmergencyContact: {
    label: "Emergency contact",
    description: "Captures a non-party contact to use in an emergency.",
  },
  ServiceMembership: {
    label: "Service registration",
    description: "Enrolls the person or family into the service.",
  },
  ServiceRoles: {
    label: "Membership roles",
    description: "Assigns one or more roles on the service membership.",
  },
  Agreements: {
    label: "Agreements",
    description: "Shows applicable service agreements during registration.",
  },
  Consents: {
    label: "Consents",
    description: "Shows applicable consent questions during registration.",
  },
  Review: {
    label: "Review",
    description: "Shows a final review step before saving.",
  },
};

function normalizeHexColour(value: string) {
  const trimmed = value.trim();
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;

  return withHash.toUpperCase();
}

function isValidHexColour(value: string) {
  return HEX_COLOUR_REGEX.test(value);
}

function getValidHexColour(value?: string | null) {
  if (!value) {
    return DEFAULT_SERVICE_COLOUR;
  }

  const normalized = normalizeHexColour(value);

  return isValidHexColour(normalized) ? normalized : DEFAULT_SERVICE_COLOUR;
}

function createRegistrationRequirement(
  memberType: RegistrationMemberType,
  componentKey: string,
  requirementLevel: RegistrationRequirementLevel,
  displayOrder: number,
  isSystemControlled = false,
): ServiceRegistrationRequirementDto {
  return {
    serviceRegistrationRequirementId: 0,
    serviceId: 0,
    memberType,
    componentKey,
    requirementLevel,
    displayOrder,
    isSystemControlled,
  };
}

function createDefaultRegistrationRequirements(
  memberType: RegistrationMemberType,
): ServiceRegistrationRequirementDto[] {
  const isServiceUser = memberType === "ServiceUser";
  const isPrimaryContact = memberType === "PrimaryContact";

  return [
    createRegistrationRequirement(memberType, "PrimaryContact", "Required", 10),
    createRegistrationRequirement(memberType, "ContactDetails", "Required", 20),
    createRegistrationRequirement(
      memberType,
      "Address",
      isServiceUser || isPrimaryContact ? "Required" : "Optional",
      30,
    ),
    createRegistrationRequirement(
      memberType,
      "Household",
      isServiceUser ? "Required" : "Hidden",
      40,
    ),
    createRegistrationRequirement(
      memberType,
      "YouthProfile",
      isServiceUser ? "Optional" : "Hidden",
      50,
    ),
    createRegistrationRequirement(
      memberType,
      "EmergencyContact",
      "Optional",
      60,
    ),
    createRegistrationRequirement(
      memberType,
      "ServiceMembership",
      "Required",
      70,
    ),
    createRegistrationRequirement(
      memberType,
      "ServiceRoles",
      isPrimaryContact ? "Hidden" : "Required",
      80,
    ),
    createRegistrationRequirement(memberType, "Agreements", "Optional", 85),
    createRegistrationRequirement(memberType, "Consents", "Optional", 86),
    createRegistrationRequirement(memberType, "Review", "Required", 1000),
  ];
}

function createDefaultRegistrationRequirementState(): Record<
  RegistrationMemberType,
  ServiceRegistrationRequirementDto[]
> {
  return {
    ServiceUser: createDefaultRegistrationRequirements("ServiceUser"),
    PrimaryContact: createDefaultRegistrationRequirements("PrimaryContact"),
    Volunteer: createDefaultRegistrationRequirements("Volunteer"),
    Staff: createDefaultRegistrationRequirements("Staff"),
  };
}

function createDefaultRegistrationJourneyState(): Record<
  RegistrationMemberType,
  "Person" | "Family"
> {
  return {
    ServiceUser: "Family",
    PrimaryContact: "Person",
    Volunteer: "Person",
    Staff: "Person",
  };
}

function mergeRegistrationRequirementsWithDefaults(
  memberType: RegistrationMemberType,
  savedRequirements?: ServiceRegistrationRequirementDto[] | null,
): ServiceRegistrationRequirementDto[] {
  const savedByComponent = new Map(
    (savedRequirements ?? []).map((requirement) => [
      requirement.componentKey.toLowerCase(),
      requirement,
    ]),
  );

  return createDefaultRegistrationRequirements(memberType)
    .map((defaultRequirement) => {
      const saved = savedByComponent.get(
        defaultRequirement.componentKey.toLowerCase(),
      );

      return saved
        ? {
            ...defaultRequirement,
            ...saved,
            memberType,
            componentKey: defaultRequirement.componentKey,
          }
        : defaultRequirement;
    })
    .sort((a, b) => a.displayOrder - b.displayOrder);
}

/* =========================
 * Component
 * ========================= */

export default function ServiceForm({
  tenantId,
  mode,
  service,
  onNameChange,
  onSaved,
  onCancel,
}: ServiceFormProps) {
  const { data } = useStaticData();
  const { hasFeature } = useAccess();
  const { selectedTenant } = useAuth();
  const canManageBranding = hasFeature(FEATURE_CODES.SERVICE_BRANDING);
  const availableSteps = SERVICE_FORM_STEPS
    .map((step, originalIndex) => ({ ...step, originalIndex }))
    .filter((step) => hasFeature(step.feature));

  const attributeCategories: ServiceAttributeCategory[] = useMemo(
    () => data?.serviceAttributeCategories ?? [],
    [data?.serviceAttributeCategories],
  );
  const serviceCategories: ServiceCategory[] = useMemo(
    () => data?.serviceCategoryGroups?.flatMap(group => group.categories) ?? data?.serviceCategories ?? [],
    [data?.serviceCategories, data?.serviceCategoryGroups],
  );
  const registrationMemberTypes = useMemo(
    () =>
      (data?.serviceRegistrationMemberTypes ?? [])
        .filter((memberType) => memberType.registrationMode === "Standalone")
        .map((memberType) => ({
          value: memberType.code,
          label: memberType.name,
        })),
    [data?.serviceRegistrationMemberTypes],
  );
  const registrationComponentText = useMemo(() => {
    const map = new Map(
      (data?.serviceRegistrationComponents ?? []).map((component) => [
        component.code.toLowerCase(),
        {
          label: component.name,
          description:
            component.description ??
            REGISTRATION_COMPONENT_FALLBACKS[component.code]?.description ??
            "",
        },
      ]),
    );

    return (componentKey: string) =>
      map.get(componentKey.toLowerCase()) ??
      REGISTRATION_COMPONENT_FALLBACKS[componentKey] ?? {
        label: componentKey,
        description: "",
      };
  }, [data?.serviceRegistrationComponents]);

  const [form, setForm] = useState<ServiceFormValue>({
    serviceKind: "Service" as "Service" | "Job",
    name: "",
    description: "",
    partyAddressId: null as number | null,
    isPublicRegistrationEnabled: true,
    usePrimaryContactForServiceUsers: false,
    isPublishedToPublicPortal: false,
    serviceCategoryId: null as number | null,
    serviceSubcategoryIds: [] as number[],
    serviceAttributeIds: [] as number[],
    roleTypeIds: [] as number[],
    colorHex: DEFAULT_SERVICE_COLOUR,
  });
  const [roleTypes, setRoleTypes] = useState<RoleType[]>([]);
  const [partyAddresses, setPartyAddresses] = useState<PartyAddress[]>([]);
  const [draftGroups, setDraftGroups] = useState<DraftServiceGroup[]>([]);
  const [draftServiceRoles, setDraftServiceRoles] = useState<
    DraftServiceRole[]
  >([]);
  const [originalCustomServiceRoles, setOriginalCustomServiceRoles] = useState<
    ServiceRoleDto[]
  >([]);
  const [draftRoleName, setDraftRoleName] = useState("");
  const [draftRoleDescription, setDraftRoleDescription] = useState("");
  const [draftRoleError, setDraftRoleError] = useState("");
  const [editingDraftRoleClientId, setEditingDraftRoleClientId] = useState<
    number | null
  >(null);
  const [activeStep, setActiveStep] = useState(0);
  const activeStepPosition = Math.max(
    0,
    availableSteps.findIndex((step) => step.originalIndex === activeStep),
  );
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [removeExistingLogo, setRemoveExistingLogo] = useState(false);
  const [portalImageFile, setPortalImageFile] = useState<File | null>(null);
  const [portalImagePreview, setPortalImagePreview] = useState<string | null>(null);
  const [savedPortalImage, setSavedPortalImage] = useState<string | null>(null);
  const [removePortalImage, setRemovePortalImage] = useState(false);
  const [portalImageError, setPortalImageError] = useState("");
  const [registrationMemberType, setRegistrationMemberType] =
    useState<RegistrationMemberType>("ServiceUser");
  const [registrationRequirements, setRegistrationRequirements] = useState<
    Record<RegistrationMemberType, ServiceRegistrationRequirementDto[]>
  >(createDefaultRegistrationRequirementState);
  const [registrationPartyTypes, setRegistrationPartyTypes] = useState<
    Record<RegistrationMemberType, "Person" | "Family">
  >(createDefaultRegistrationJourneyState);
  const [loadingRegistrationRequirements, setLoadingRegistrationRequirements] =
    useState(false);
  const [agreementTemplates, setAgreementTemplates] = useState<
    ServiceAgreementTemplateDto[]
  >([]);
  const [loadingAgreementTemplates, setLoadingAgreementTemplates] =
    useState(false);

  useEffect(() => {
    onNameChange(form.name);
  }, [form.name, onNameChange]);

  useEffect(() => {
    if (
      registrationMemberTypes.length > 0 &&
      !registrationMemberTypes.some(
        (memberType) => memberType.value === registrationMemberType,
      )
    ) {
      setRegistrationMemberType(registrationMemberTypes[0].value);
    }
  }, [registrationMemberType, registrationMemberTypes]);
  const selectableRoleTypes = useMemo(
    () =>
      roleTypes.filter(
        (roleType) =>
          roleType.roleName.trim().toLowerCase() !==
          STANDARD_SERVICE_USER_ROLE_NAME.toLowerCase(),
      ),
    [roleTypes],
  );

  /* =========================
   * Load role type catalogue
   * ========================= */

  useEffect(() => {
    let cancelled = false;

    async function loadRoleTypes() {
      try {
        const result = await api<RoleType[]>("/api/static/roletypes");

        if (!cancelled) {
          setRoleTypes(result ?? []);
        }
      } catch (error) {
        console.error("Failed to load role types", error);
      }
    }

    loadRoleTypes();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadPartyAddresses() {
      try {
        const result = await api<PartyAddress[]>("/api/services/addresses");
        if (!cancelled) {
          setPartyAddresses(result ?? []);
        }
      } catch (error) {
        console.error("Failed to load service addresses", error);
        if (!cancelled) {
          setPartyAddresses([]);
        }
      }
    }

    if (tenantId) {
      loadPartyAddresses();
    }

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  /* =========================
   * Populate edit / create mode
   * ========================= */

  useEffect(() => {
    setActiveStep(0);

    if (mode === "edit" && service) {
      setDraftServiceRoles([]);
      setOriginalCustomServiceRoles([]);
      setDraftRoleName("");
      setDraftRoleDescription("");
      setDraftRoleError("");
      setEditingDraftRoleClientId(null);
      const colorHex = getValidHexColour(service.colorHex);

      setForm({
        serviceKind: service.serviceKind ?? "Service",
        name: service.name,
        description: service.description ?? "",
        partyAddressId: service.partyAddressId ?? null,
        isPublicRegistrationEnabled: service.isPublicRegistrationEnabled,
        usePrimaryContactForServiceUsers: service.usePrimaryContactForServiceUsers ?? false,
        isPublishedToPublicPortal: service.isPublishedToPublicPortal ?? false,
        serviceCategoryId: service.serviceCategoryId ?? null,
        serviceSubcategoryIds: service.serviceSubcategoryIds ?? [],
        primaryServiceSubcategoryId: service.primaryServiceSubcategoryId ?? null,
        serviceAttributeIds: service.serviceAttributeIds,
        roleTypeIds: service.roleTypeIds ?? [],
        colorHex,
      });
      setLogoFile(null);
      setLogoPreviewUrl(null);
      setRemoveExistingLogo(false);
      setRegistrationRequirements(createDefaultRegistrationRequirementState());
      setRegistrationPartyTypes(createDefaultRegistrationJourneyState());
      setAgreementTemplates([]);
    }

    if (mode === "create") {
      setForm({
        serviceKind: "Service",
        name: "",
        description: "",
        partyAddressId: null,
        isPublicRegistrationEnabled: true,
        usePrimaryContactForServiceUsers: false,
        isPublishedToPublicPortal: false,
        serviceCategoryId: null,
        serviceSubcategoryIds: [],
        serviceAttributeIds: [],
        roleTypeIds: [],
        colorHex: DEFAULT_SERVICE_COLOUR,
      });
      setDraftGroups([]);
      setDraftServiceRoles([]);
      setOriginalCustomServiceRoles([]);
      setDraftRoleName("");
      setDraftRoleDescription("");
      setDraftRoleError("");
      setEditingDraftRoleClientId(null);
      setLogoFile(null);
      setLogoPreviewUrl(null);
      setRemoveExistingLogo(false);
      setRegistrationRequirements(createDefaultRegistrationRequirementState());
      setRegistrationPartyTypes(createDefaultRegistrationJourneyState());
      setAgreementTemplates([]);
    }
  }, [mode, service]);

  useEffect(() => {
    if (mode !== "edit" || !service?.serviceId) {
      return;
    }

    let cancelled = false;
    const serviceId = service.serviceId;

    async function loadRegistrationRequirements() {
      try {
        setLoadingRegistrationRequirements(true);
        const [entries, journeys] = await Promise.all([
          Promise.all(
            registrationMemberTypes.map(async (memberType) => {
              const result = await api<ServiceRegistrationRequirementDto[]>(
                `/api/services/${serviceId}/registration-requirements?memberType=${memberType.value}`,
                {
                  suppressLoading: true,
                  notifyError: false,
                },
              );
              return [memberType.value, result] as const;
            }),
          ),
          api<ServiceRegistrationJourneyDto[]>(
            `/api/services/${serviceId}/registration-journeys`,
            {
              suppressLoading: true,
              notifyError: false,
            },
          ),
        ]);

        if (cancelled) return;

        setRegistrationRequirements({
          ...createDefaultRegistrationRequirementState(),
          ...(Object.fromEntries(
            registrationMemberTypes.map((memberType) => [
              memberType.value,
              mergeRegistrationRequirementsWithDefaults(
                memberType.value,
                entries.find(
                  ([entryMemberType]) => entryMemberType === memberType.value,
                )?.[1],
              ),
            ]),
          ) as Partial<
            Record<RegistrationMemberType, ServiceRegistrationRequirementDto[]>
          >),
        });
        setRegistrationPartyTypes({
          ...createDefaultRegistrationJourneyState(),
          ...(Object.fromEntries(
            journeys.map((journey) => [journey.memberType, journey.partyType]),
          ) as Partial<Record<RegistrationMemberType, "Person" | "Family">>),
        });
      } catch (error) {
        console.error(
          "Failed to load service registration requirements",
          error,
        );
      } finally {
        if (!cancelled) {
          setLoadingRegistrationRequirements(false);
        }
      }
    }

    loadRegistrationRequirements();

    return () => {
      cancelled = true;
    };
  }, [mode, registrationMemberTypes, service?.serviceId]);

  useEffect(() => {
    if (mode !== "edit" || !service?.serviceId) return;

    let cancelled = false;
    const serviceId = service.serviceId;

    async function loadAgreementTemplates() {
      try {
        setLoadingAgreementTemplates(true);
        const result = await api<ServiceAgreementTemplateDto[]>(
          `/api/services/${serviceId}/agreement-templates`,
          { suppressLoading: true, notifyError: false },
        );
        if (!cancelled) setAgreementTemplates(result ?? []);
      } catch (error) {
        console.error("Failed to load agreement templates", error);
        if (!cancelled) setAgreementTemplates([]);
      } finally {
        if (!cancelled) setLoadingAgreementTemplates(false);
      }
    }

    loadAgreementTemplates();
    return () => {
      cancelled = true;
    };
  }, [mode, service?.serviceId]);

  useEffect(() => {
    return () => {
      if (logoPreviewUrl) URL.revokeObjectURL(logoPreviewUrl);
    };
  }, [logoPreviewUrl]);

  useEffect(() => () => { if (portalImagePreview) URL.revokeObjectURL(portalImagePreview); }, [portalImagePreview]);
  useEffect(() => {
    setPortalImageFile(null); setPortalImagePreview(null); setRemovePortalImage(false); setPortalImageError("");
    setSavedPortalImage(null);
    if (mode !== "edit" || !service?.hasPortalImage || !canManageBranding) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    api<Blob>(`/api/services/${service.serviceId}/portal-image`, { responseType: "blob", suppressLoading: true, notifyError: false })
      .then(blob => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSavedPortalImage(objectUrl);
      }).catch(() => { if (!cancelled) setPortalImageError("The saved portal image could not be loaded. You can retry by reopening this service."); });
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [mode, service?.serviceId, service?.hasPortalImage, canManageBranding]);

  useEffect(() => {
    if (mode !== "edit" || !service?.serviceId) {
      return;
    }

    if (roleTypes.length === 0) {
      return;
    }

    let cancelled = false;
    const serviceId = service.serviceId;

    async function loadServiceRoles() {
      try {
        const roles = await api<ServiceRoleDto[]>(
          `/api/services/${serviceId}/roles`,
        );

        if (cancelled) {
          return;
        }

        const activeRoleNames = new Set(
          roles
            .filter((role) => role.isActive)
            .map((role) => role.name.trim().toLowerCase()),
        );
        const selectedRoleTypeIds = roleTypes
          .filter(
            (roleType) =>
              roleType.roleName.trim().toLowerCase() !==
                STANDARD_SERVICE_USER_ROLE_NAME.toLowerCase() &&
              activeRoleNames.has(roleType.roleName.trim().toLowerCase()),
          )
          .map((roleType) => roleType.roleTypeId);
        const catalogueRoleNames = new Set(
          roleTypes.map((roleType) => roleType.roleName.trim().toLowerCase()),
        );
        const customRoles = roles.filter(
          (role) =>
            role.isActive &&
            role.name.trim().toLowerCase() !==
              STANDARD_SERVICE_USER_ROLE_NAME.toLowerCase() &&
            !catalogueRoleNames.has(role.name.trim().toLowerCase()),
        );

        setForm((prev) => ({
          ...prev,
          roleTypeIds: selectedRoleTypeIds,
        }));
        setOriginalCustomServiceRoles(customRoles);
        setDraftServiceRoles(
          customRoles.map((role) => ({
            clientId: role.serviceRoleId,
            serviceRoleId: role.serviceRoleId,
            name: role.name,
            description: role.description ?? "",
          })),
        );
      } catch (error) {
        console.error("Failed to load service roles", error);
      }
    }

    loadServiceRoles();

    return () => {
      cancelled = true;
    };
  }, [mode, roleTypes, service?.serviceId]);

  /* =========================
   * Handlers
   * ========================= */

  function toggleAttribute(attributeId: number) {
    const audienceCategory = attributeCategories.find(
      (category) =>
        category.code?.toUpperCase() === "AUDIENCE" ||
        category.name.trim().toLowerCase() === "audience",
    );
    const audienceAttributeIds = new Set(
      audienceCategory?.attributes.map(
        (attribute) => attribute.serviceAttributeId,
      ) ?? [],
    );
    const everyoneAttribute = audienceCategory?.attributes.find(
      (attribute) =>
        attribute.code?.toUpperCase() === "EVERYONE" ||
        attribute.name.trim().toLowerCase() === "everyone",
    );

    setForm((prev) => {
      if (prev.serviceAttributeIds.includes(attributeId)) {
        return {
          ...prev,
          serviceAttributeIds: prev.serviceAttributeIds.filter(
            (id) => id !== attributeId,
          ),
        };
      }

      let serviceAttributeIds = [...prev.serviceAttributeIds];

      if (everyoneAttribute && audienceAttributeIds.has(attributeId)) {
        serviceAttributeIds =
          attributeId === everyoneAttribute.serviceAttributeId
            ? serviceAttributeIds.filter((id) => !audienceAttributeIds.has(id))
            : serviceAttributeIds.filter(
                (id) => id !== everyoneAttribute.serviceAttributeId,
              );
      }

      return {
        ...prev,
        serviceAttributeIds: [...serviceAttributeIds, attributeId],
      };
    });
  }

  function selectServiceCategory(serviceCategoryId: number | null) {
    const validSubcategoryIds = new Set(
      serviceCategories
        .find((category) => category.serviceCategoryId === serviceCategoryId)
        ?.subcategories.map(
          (subcategory) => subcategory.serviceSubcategoryId,
        ) ?? [],
    );

    setForm((prev) => ({
      ...prev,
      serviceCategoryId,
      primaryServiceSubcategoryId: validSubcategoryIds.has(prev.primaryServiceSubcategoryId ?? 0)
        ? prev.primaryServiceSubcategoryId : null,
    }));
  }

  function toggleRoleType(roleTypeId: number) {
    const roleType = selectableRoleTypes.find(
      (item) => item.roleTypeId === roleTypeId,
    );
    const isSelected = form.roleTypeIds.includes(roleTypeId);
    const matchesDraftRole = roleType
      ? draftServiceRoles.some(
          (role) =>
            role.name.trim().toLowerCase() ===
            roleType.roleName.trim().toLowerCase(),
        )
      : false;

    if (!isSelected && matchesDraftRole) {
      setDraftRoleError(
        `${roleType?.roleName ?? "That role"} has already been added as a service-specific role.`,
      );
      return;
    }

    setForm((prev) => ({
      ...prev,
      roleTypeIds: prev.roleTypeIds.includes(roleTypeId)
        ? prev.roleTypeIds.filter((id) => id !== roleTypeId)
        : [...prev.roleTypeIds, roleTypeId],
    }));
  }

  function addDraftServiceRole() {
    const name = draftRoleName.trim();
    const normalizedName = name.toLowerCase();

    if (!name) {
      setDraftRoleError("Enter a role name.");
      return;
    }

    const matchesDraftRole = draftServiceRoles.some(
      (role) =>
        role.clientId !== editingDraftRoleClientId &&
        role.name.trim().toLowerCase() === normalizedName,
    );
    const matchesSelectedRoleType = selectableRoleTypes.some(
      (roleType) =>
        form.roleTypeIds.includes(roleType.roleTypeId) &&
        roleType.roleName.trim().toLowerCase() === normalizedName,
    );

    if (matchesDraftRole || matchesSelectedRoleType) {
      setDraftRoleError("That role has already been added.");
      return;
    }

    setDraftServiceRoles((current) =>
      editingDraftRoleClientId === null
        ? [
            ...current,
            {
              clientId: Date.now(),
              name,
              description: draftRoleDescription.trim(),
            },
          ]
        : current.map((role) =>
            role.clientId === editingDraftRoleClientId
              ? {
                  ...role,
                  name,
                  description: draftRoleDescription.trim(),
                }
              : role,
          ),
    );
    setDraftRoleName("");
    setDraftRoleDescription("");
    setDraftRoleError("");
    setEditingDraftRoleClientId(null);
  }

  function removeDraftServiceRole(clientId: number) {
    setDraftServiceRoles((current) =>
      current.filter((role) => role.clientId !== clientId),
    );
    if (editingDraftRoleClientId === clientId) {
      setDraftRoleName("");
      setDraftRoleDescription("");
      setEditingDraftRoleClientId(null);
    }
  }

  function editDraftServiceRole(role: DraftServiceRole) {
    setDraftRoleName(role.name);
    setDraftRoleDescription(role.description);
    setDraftRoleError("");
    setEditingDraftRoleClientId(role.clientId);
  }

  function updateRegistrationRequirementLevel(
    componentKey: string,
    requirementLevel: RegistrationRequirementLevel,
  ) {
    setRegistrationRequirements((current) => ({
      ...current,
      [registrationMemberType]: current[registrationMemberType].map(
        (requirement) =>
          requirement.componentKey === componentKey
            ? { ...requirement, requirementLevel }
            : requirement,
      ),
    }));
  }

  async function saveRegistrationRequirements(serviceId: number) {
    for (const memberType of registrationMemberTypes) {
      await api(`/api/services/${serviceId}/registration-requirements`, {
        method: "PUT",
        body: {
          memberType: memberType.value,
          requirements: registrationRequirements[memberType.value].map(
            (requirement) => ({
              ...requirement,
              serviceId,
              memberType: memberType.value,
            }),
          ),
        },
      });
    }
  }

  async function saveRegistrationJourneys(serviceId: number) {
    await api(`/api/services/${serviceId}/registration-journeys`, {
      method: "PUT",
      body: {
        journeys: registrationMemberTypes.map((memberType) => ({
          serviceId,
          memberType: memberType.value,
          partyType: registrationPartyTypes[memberType.value],
        })),
      },
    });
  }

  async function saveAgreementTemplates(serviceId: number) {
    await api(`/api/services/${serviceId}/agreement-templates`, {
      method: "PUT",
      body: {
        templates: agreementTemplates.map((template, index) => ({
          ...template,
          serviceId,
          displayOrder: (index + 1) * 10,
          isActive: true,
        })),
      },
    });
  }

  function updateColour(value: string) {
    const colorHex = getValidHexColour(value);

    setForm((prev) => ({
      ...prev,
      colorHex,
    }));
  }

  async function handleSubmit() {
    if (!form.name.trim() || (!form.serviceCategoryId && !canRetainUnclassifiedService)) {
      alert("Service name and category are required");
      return;
    }

    if (registrationMemberTypes.length === 0) {
      alert("No registration types are currently available.");
      return;
    }

    if (serviceCategories.length > 0 && !form.serviceCategoryId && !canRetainUnclassifiedService) {
      setActiveStep(2);
      alert("Select a main service category.");
      return;
    }

    if ((mode === "create" || form.serviceSubcategoryIds.length > 0 ||
      form.serviceCategoryId !== (service?.serviceCategoryId ?? null)) && !form.primaryServiceSubcategoryId) {
      setActiveStep(0);
      alert("Select a primary service subcategory.");
      return;
    }

    const audienceCategory = attributeCategories.find(
      (category) =>
        category.code?.toUpperCase() === "AUDIENCE" ||
        category.name.trim().toLowerCase() === "audience",
    );
    const everyoneAttribute = audienceCategory?.attributes.find(
      (attribute) =>
        attribute.code?.toUpperCase() === "EVERYONE" ||
        attribute.name.trim().toLowerCase() === "everyone",
    );
    const selectedAudienceIds =
      audienceCategory?.attributes
        .filter((attribute) =>
          form.serviceAttributeIds.includes(attribute.serviceAttributeId),
        )
        .map((attribute) => attribute.serviceAttributeId) ?? [];

    if (
      everyoneAttribute &&
      selectedAudienceIds.includes(everyoneAttribute.serviceAttributeId) &&
      selectedAudienceIds.length > 1
    ) {
      setActiveStep(2);
      alert("'Everyone' cannot be combined with another audience.");
      return;
    }

    const incompleteAgreement = agreementTemplates.find(
      (template) =>
        !template.name.trim() ||
        !template.text.trim() ||
        template.memberTypes.length === 0,
    );
    const hasDuplicateAgreementName = agreementTemplates.some(
      (template, index) =>
        agreementTemplates.findIndex(
          (candidate) =>
            candidate.name.trim().toLowerCase() ===
            template.name.trim().toLowerCase(),
        ) !== index,
    );
    if (incompleteAgreement || hasDuplicateAgreementName) {
      setActiveStep(4);
      alert(
        hasDuplicateAgreementName
          ? "Agreement and consent names must be unique."
          : "Each agreement or consent needs a name, wording and at least one member type.",
      );
      return;
    }

    const colorHex = normalizeHexColour(form.colorHex);

    if (!isValidHexColour(colorHex)) {
      alert("Please choose a valid six-digit hex service colour");
      return;
    }

    const rolesToCreate = [...draftServiceRoles];
    const pendingRoleName = draftRoleName.trim();

    if (pendingRoleName) {
      const normalizedPendingName = pendingRoleName.toLowerCase();
      const pendingRoleExists = rolesToCreate.some(
        (role) =>
          role.clientId !== editingDraftRoleClientId &&
          role.name.trim().toLowerCase() === normalizedPendingName,
      );
      const matchesSelectedRoleType = selectableRoleTypes.some(
        (roleType) =>
          form.roleTypeIds.includes(roleType.roleTypeId) &&
          roleType.roleName.trim().toLowerCase() === normalizedPendingName,
      );

      if (pendingRoleExists || matchesSelectedRoleType) {
        setActiveStep(5);
        setDraftRoleError("That role has already been added.");
        return;
      }

      if (editingDraftRoleClientId !== null) {
        const roleIndex = rolesToCreate.findIndex(
          (role) => role.clientId === editingDraftRoleClientId,
        );
        if (roleIndex >= 0) {
          rolesToCreate[roleIndex] = {
            ...rolesToCreate[roleIndex],
            name: pendingRoleName,
            description: draftRoleDescription.trim(),
          };
        }
      } else {
        rolesToCreate.push({
          clientId: Date.now(),
          name: pendingRoleName,
          description: draftRoleDescription.trim(),
        });
      }
    }

    const payload = {
      serviceKind: form.serviceKind,
      name: form.name,
      description: form.description,
      partyAddressId: form.partyAddressId,
      isPublicRegistrationEnabled: form.isPublicRegistrationEnabled,
      usePrimaryContactForServiceUsers: form.usePrimaryContactForServiceUsers ?? false,
      isPublishedToPublicPortal: form.isPublishedToPublicPortal ?? false,
      serviceCategoryId: form.serviceCategoryId,
      serviceSubcategoryIds: form.serviceSubcategoryIds,
      primaryServiceSubcategoryId: form.primaryServiceSubcategoryId,
      serviceAttributeIds: form.serviceAttributeIds,
      roleTypeIds: form.roleTypeIds.filter((roleTypeId) =>
        selectableRoleTypes.some(
          (roleType) => roleType.roleTypeId === roleTypeId,
        ),
      ),
      colorHex,
    };

    const savedService = await api<ServiceDto>(
      mode === "create"
        ? `/api/services/create-service?tenantId=${tenantId}`
        : `/api/services/${service!.serviceId}?tenantId=${tenantId}`,
      {
        method: mode === "create" ? "POST" : "PUT",
        body: payload,
      },
    );

    const savedServiceId =
      mode === "create" ? savedService.serviceId : service!.serviceId;

    if (portalImageFile) {
      const imageData = new FormData();
      imageData.append("file", portalImageFile);
      await api(`/api/services/${savedServiceId}/portal-image`, { method: "POST", body: imageData });
    } else if (mode === "edit" && removePortalImage && service?.hasPortalImage) {
      await api(`/api/services/${savedServiceId}/portal-image`, { method: "DELETE" });
    }

    if (mode === "edit" && removeExistingLogo && service?.hasLogo) {
      await api(`/api/services/${savedServiceId}/logo`, { method: "DELETE" });
    }

    if (logoFile) {
      const logoData = new FormData();
      logoData.append("file", logoFile);
      await api(`/api/services/${savedServiceId}/logo`, {
        method: "POST",
        body: logoData,
      });
    }

    if (mode === "create" && draftGroups.length > 0) {
      for (const group of draftGroups.filter((item) => item.isActive)) {
        await api(`/api/services/${savedService.serviceId}/groups`, {
          method: "POST",
          body: {
            name: group.name,
            description: group.description ?? null,
            capacity: group.capacity ?? null,
          },
        });
      }
    }

    if (mode === "create" && rolesToCreate.length > 0) {
      for (const role of rolesToCreate) {
        await api<ServiceRoleDto>(`/api/services/${savedServiceId}/roles`, {
          method: "POST",
          body: {
            name: role.name,
            description: role.description,
          },
        });
      }
    }

    if (mode === "edit") {
      const retainedRoleIds = new Set(
        rolesToCreate
          .map((role) => role.serviceRoleId)
          .filter((roleId): roleId is number => roleId !== undefined),
      );

      for (const originalRole of originalCustomServiceRoles) {
        if (!retainedRoleIds.has(originalRole.serviceRoleId)) {
          await api<void>(`/api/services/roles/${originalRole.serviceRoleId}`, {
            method: "DELETE",
          });
        }
      }

      for (const role of rolesToCreate) {
        if (role.serviceRoleId) {
          const originalRole = originalCustomServiceRoles.find(
            (item) => item.serviceRoleId === role.serviceRoleId,
          );
          const hasChanged =
            originalRole &&
            (originalRole.name !== role.name ||
              (originalRole.description ?? "") !== role.description);

          if (hasChanged) {
            await api<ServiceRoleDto>(
              `/api/services/roles/${role.serviceRoleId}`,
              {
                method: "PUT",
                body: {
                  name: role.name,
                  description: role.description,
                  isActive: true,
                },
              },
            );
          }
        } else {
          await api<ServiceRoleDto>(`/api/services/${savedServiceId}/roles`, {
            method: "POST",
            body: {
              name: role.name,
              description: role.description,
            },
          });
        }
      }
    }

    await saveRegistrationJourneys(savedServiceId);
    await saveRegistrationRequirements(savedServiceId);
    await saveAgreementTemplates(savedServiceId);

    onSaved();
  }

  const canRetainUnclassifiedService = mode === "edit" && service?.serviceCategoryId == null &&
    form.serviceCategoryId == null && form.serviceSubcategoryIds.length === 0 &&
    !form.primaryServiceSubcategoryId;
  const hasRequiredDetails = Boolean(
    form.name.trim() && (form.serviceCategoryId || canRetainUnclassifiedService) &&
    (form.primaryServiceSubcategoryId || (mode === "edit" &&
      (service?.serviceSubcategoryIds?.length ?? 0) === 0 &&
      form.serviceCategoryId === (service?.serviceCategoryId ?? null) &&
      form.serviceSubcategoryIds.length === 0)),
  );
  const existingLogoUrl =
    mode === "edit" && service?.hasLogo && !removeExistingLogo
      ? `${config.API_BASE_URL}${
          service.logoUrl ?? `/api/services/${service.serviceId}/logo`
        }`
      : null;
  function selectLogo(file: File | undefined) {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      alert("Choose a PNG, JPEG or WebP image.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert("The service logo must be 2 MB or smaller.");
      return;
    }

    setLogoFile(file);
    setLogoPreviewUrl(URL.createObjectURL(file));
    setRemoveExistingLogo(false);
  }

  function removeLogo() {
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setRemoveExistingLogo(Boolean(service?.hasLogo));
  }

  function goToStep(stepIndex: number) {
    if (stepIndex > 0 && !hasRequiredDetails) {
      return;
    }

    setActiveStep(stepIndex);
  }

  /* =========================
   * Render
   * ========================= */

  return (
    <div>
      <Box
        className="service-form-desktop-stepper"
        sx={{ width: "100%", mb: 3 }}
      >
        <Stepper activeStep={activeStepPosition} alternativeLabel>
          {availableSteps.map((step, index) => (
            <Step key={step.label}>
              <StepButton
                onClick={() => goToStep(step.originalIndex)}
                disabled={index > 0 && !hasRequiredDetails}
                sx={{
                  "& .MuiStepLabel-label": {
                    color:
                      index > 0 && !hasRequiredDetails ? "#9ca3af" : "inherit",
                  },
                }}
              >
                {step.label}
              </StepButton>
            </Step>
          ))}
        </Stepper>
      </Box>

      <div className="service-form-mobile-stepper">
        <label className="service-form-mobile-step-select">
          <select
            value={activeStep}
            onChange={(event) => goToStep(Number(event.target.value))}
            aria-label="Go to form step"
          >
            {availableSteps.map((step, index) => (
              <option
                key={step.label}
                value={step.originalIndex}
                disabled={index > 0 && !hasRequiredDetails}
              >
                {index + 1}. {step.label}
              </option>
            ))}
          </select>
        </label>
        <span className="service-form-mobile-step-count">
          Step {activeStepPosition + 1}/{availableSteps.length}
        </span>
        <div
          className="service-form-mobile-progress"
          role="progressbar"
          aria-label="Service form progress"
          aria-valuemin={1}
          aria-valuemax={availableSteps.length}
          aria-valuenow={activeStepPosition + 1}
        >
          <span
            style={{
              width: `${((activeStepPosition + 1) / availableSteps.length) * 100}%`,
            }}
          />
        </div>
      </div>

      {activeStep === 0 && (
        <ServiceDetailsStep
          value={form}
          partyAddresses={partyAddresses}
          categories={serviceCategories}
          groups={data?.serviceCategoryGroups ?? []}
          onChange={setForm}
          onCategoryChange={selectServiceCategory}
        />
      )}
      {activeStep === 2 && (
        <ServiceBrandingStep
          colorHex={form.colorHex}
          logoUrl={logoPreviewUrl ?? existingLogoUrl}
          onColourChange={updateColour}
          onLogoSelect={selectLogo}
          onLogoRemove={removeLogo}
          portalImageUrl={portalImagePreview ?? (removePortalImage ? null : savedPortalImage)}
          hasPortalImage={!!portalImageFile || (!!service?.hasPortalImage && !removePortalImage)}
          portalImageError={portalImageError}
          onPortalImageSelect={(file) => {
            if (!file) return;
            if (!["image/png", "image/jpeg", "image/webp"].includes(file.type) || file.size > 5 * 1024 * 1024) {
              setPortalImageError("Choose a PNG, JPEG or WebP image, up to 5 MB."); return;
            }
            setPortalImageError(""); setPortalImageFile(file); setPortalImagePreview(URL.createObjectURL(file)); setRemovePortalImage(false);
          }}
          onPortalImageRemove={() => { setPortalImageFile(null); setPortalImagePreview(null); setRemovePortalImage(true); setPortalImageError(""); }}
          serviceName={form.name}
          organisationName={selectedTenant?.tenantName}
          serviceDescription={form.description}
          portalPublished={form.isPublishedToPublicPortal ?? false}
          canGeneratePortalImage={hasFeature(FEATURE_CODES.AI_INTEGRATION)}
          portalAttributes={attributeCategories.flatMap(category => category.attributes.filter(attribute => form.serviceAttributeIds.includes(attribute.serviceAttributeId)).map(attribute => attribute.name))}
          portalAddress={(() => { const address = partyAddresses.find(a => a.partyAddressId === form.partyAddressId)?.address; return address ? [address.addressLine1, address.town, address.postCode].filter(Boolean).join(", ") : "Contact the provider for venue details"; })()}
        />
      )}
      {activeStep === 1 && (
        <ServiceAttributesStep
          value={form}
          attributeCategories={attributeCategories}
          onAttributeToggle={toggleAttribute}
        />
      )}
      {activeStep === 3 && (
        <ServiceRegistrationStep
          memberTypes={registrationMemberTypes}
          publicRegistrationEnabled={form.isPublicRegistrationEnabled}
          memberType={registrationMemberType}
          requirements={registrationRequirements}
          partyTypes={registrationPartyTypes}
          loading={loadingRegistrationRequirements}
          componentText={registrationComponentText}
          onMemberTypeChange={setRegistrationMemberType}
          onPublicRegistrationChange={(enabled) =>
            setForm((current) => ({
              ...current,
              isPublicRegistrationEnabled: enabled,
            }))
          }
          onPartyTypeChange={(partyType) =>
            setRegistrationPartyTypes((current) => ({
              ...current,
              [registrationMemberType]: partyType,
            }))
          }
          onRequirementChange={updateRegistrationRequirementLevel}
        />
      )}
      {activeStep === 4 && (
        <ServiceAgreementsStep
          value={agreementTemplates}
          loading={loadingAgreementTemplates}
          onChange={setAgreementTemplates}
        />
      )}
      {activeStep === 5 && (
        <ServiceTeamRolesStep
          roleTypes={selectableRoleTypes}
          selectedRoleTypeIds={form.roleTypeIds}
          roles={draftServiceRoles}
          name={draftRoleName}
          description={draftRoleDescription}
          error={draftRoleError}
          editingId={editingDraftRoleClientId}
          onRoleTypeToggle={toggleRoleType}
          onNameChange={(name) => {
            setDraftRoleName(name);
            setDraftRoleError("");
          }}
          onDescriptionChange={setDraftRoleDescription}
          onAdd={addDraftServiceRole}
          onEdit={editDraftServiceRole}
          onRemove={removeDraftServiceRole}
        />
      )}
      {activeStep === 6 && (
        <ServiceGroupsStep
          serviceId={mode === "edit" ? service?.serviceId : undefined}
          groups={draftGroups}
          onChange={setDraftGroups}
        />
      )}

      <div
        className="button-row service-form-actions"
        style={{ marginTop: 16 }}
      >
        <button
          type="button"
          className="action-button secondary"
          onClick={() =>
            setActiveStep(
              availableSteps[Math.max(activeStepPosition - 1, 0)].originalIndex,
            )
          }
          disabled={activeStepPosition === 0}
        >
          <ArrowBackIcon fontSize="small" />
          Back
        </button>

        {activeStepPosition < availableSteps.length - 1 && (
          <button
            type="button"
            className="action-button"
            onClick={() =>
              setActiveStep(availableSteps[activeStepPosition + 1].originalIndex)
            }
            disabled={!hasRequiredDetails}
            title={
              hasRequiredDetails
                ? undefined
                : "Enter a service name and select a category to continue."
            }
          >
            Next
            <ArrowForwardIcon fontSize="small" />
          </button>
        )}

        <button
          type="button"
          className="form-submit-button service-form-save"
          onClick={handleSubmit}
          disabled={!hasRequiredDetails}
          title={
            hasRequiredDetails
              ? undefined
              : "Service name and category are required before saving."
          }
        >
          {mode === "create" ? "Create Service" : "Save"}
        </button>

        <button
          type="button"
          className="form-submit-button service-form-cancel"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
